import {Component} from "react";
import {Button, Drawer, message, Popconfirm, Space, Table, Tag} from "antd";
import {DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined} from "@ant-design/icons";
import {WindowParamContext} from "../../../../../library/context/WindowContext";
import {
    addSystemMessage,
    AddSystemMessageReq,
    allSystemMessageRoleList,
    deleteSystemMessage,
    DeleteSystemMessageReq,
    emptySystemMessage,
    emptySystemMessagePaginationData,
    formatMessageContent,
    getSystemMessageRoleDesc,
    parseMessageContent,
    querySystemMessageByPage,
    QuerySystemMessageByPageReq,
    SystemMessageModel,
    SystemMessagePaginationData,
    updateSystemMessage,
    UpdateSystemMessageReq
} from "../../../../data/systemMessage";
import {ColumnUnit} from "../../../../../library/atomic/unit/ColumnUnit";
import {SizeAttr} from "../../../../../library/basic/size";
import {ColorAttr} from "../../../../../library/basic/color";
import {ColumnAttr, RowAttr} from "../../../../../library/basic/compose";
import {ModifyInputSelect, ModifyInputString, ModifyInputTextArea} from "../ModifyInput";
import {RowUnit} from "../../../../../library/atomic/unit/RowUnit";
import {SpanText} from "../../../../../library/atomic/text/SpanText";
import {FontAttr} from "../../../../../library/basic/font";
import {InputSelect, InputString} from "../FilterInput";
import {heightGap} from "../../../../../library/atomic/unit/BoxUnit";
import {ColumnsType} from "antd/es/table";

interface SystemMessagePanelState {
    detail_id: string;
    query_request: QuerySystemMessageByPageReq;
    query_data: SystemMessagePaginationData;
    op_request: {
        id: string;
        user_id: string;
        role: string;
        message_type: string;
        message_content: string;
        text_content: string; // 用于编辑文本内容
    };
}

export class SystemMessagePanel extends Component<{}, SystemMessagePanelState> {
    static contextType = WindowParamContext;

    state: SystemMessagePanelState = {
        detail_id: "-1",
        query_request: {
            user_id: "0",
            role: "",
            message_type: "",
            message_content: "",
            page_num: 1,
            page_size: 10,
        },
        query_data: emptySystemMessagePaginationData,
        op_request: {
            id: "",
            user_id: "0",
            role: "system",
            message_type: "text",
            message_content: "",
            text_content: "",
        }
    }

    queryCertainPage(pageNum: number, pageSize: number) {
        let query_request = Object.assign({}, this.state.query_request, {
            page_num: pageNum,
            page_size: pageSize,
        });
        querySystemMessageByPage(query_request, (success, resp) => {
            if (success && resp) {
                this.setState(prevState => (Object.assign(prevState,
                    {
                        query_request: query_request,
                        query_data: resp.data
                    }
                )))
            } else {
                message.error("查询失败");
            }
        })
    }

    handleOperateClose() {
        this.setState(prevState => (Object.assign(prevState, {detail_id: "-1"})))
    }

    handleAddOrUpdateConfirm() {
        if (this.state.op_request.role.length === 0) {
            message.warning("角色不能为空");
            return;
        }
        if (this.state.op_request.message_type.length === 0) {
            message.warning("消息类型不能为空");
            return;
        }
        if (this.state.op_request.message_type === "text" && this.state.op_request.text_content.trim().length === 0) {
            message.warning("消息内容不能为空");
            return;
        }

        const messageContent = formatMessageContent({text: this.state.op_request.text_content});

        if (this.state.detail_id === "0") {
            // 新增
            const req: AddSystemMessageReq = {
                user_id: this.state.op_request.user_id,
                role: this.state.op_request.role,
                message_type: this.state.op_request.message_type,
                message_content: messageContent,
            };
            addSystemMessage(req, (success, resp) => {
                if (success) {
                    message.success("新增成功");
                    this.handleOperateClose();
                    this.queryCertainPage(this.state.query_request.page_num, this.state.query_request.page_size);
                } else {
                    message.error(resp?.msg ? resp?.msg : "操作失败");
                }
            });
        } else {
            // 更新
            const req: UpdateSystemMessageReq = {
                id: this.state.op_request.id,
                role: this.state.op_request.role,
                message_type: this.state.op_request.message_type,
                message_content: messageContent,
            };
            updateSystemMessage(req, (success, resp) => {
                if (success) {
                    message.success("更新成功");
                    this.handleOperateClose();
                    this.queryCertainPage(this.state.query_request.page_num, this.state.query_request.page_size);
                } else {
                    message.error(resp?.msg ? resp?.msg : "操作失败");
                }
            });
        }
    }

    handleAddNewMessage() {
        this.setState(prevState => (Object.assign(prevState, {
            detail_id: "0",
            op_request: {
                id: "",
                user_id: "0",
                role: "system",
                message_type: "text",
                message_content: "",
                text_content: "",
            }
        })))
    }

    handleMessageViewDetail(id: string) {
        let msg = this.findDetailEntity(id);
        const content = parseMessageContent(msg.message_content);
        this.setState(prevState => (Object.assign(prevState, {
            detail_id: id,
            op_request: {
                id: msg.id,
                user_id: msg.user_id,
                role: msg.role,
                message_type: msg.message_type,
                message_content: msg.message_content,
                text_content: content.text,
            }
        })))
    }

    handleDeleteMessage(id: string) {
        const req: DeleteSystemMessageReq = {id: id};
        deleteSystemMessage(req, (success, resp) => {
            if (success) {
                message.success("删除成功");
                this.queryCertainPage(this.state.query_request.page_num, this.state.query_request.page_size);
            } else {
                message.error(resp?.msg ? resp?.msg : "删除失败");
            }
        });
    }

    findDetailEntity(detail_id: string): SystemMessageModel {
        for (let i = 0; i < this.state.query_data.list.length; i++) {
            if (this.state.query_data.list[i].id === detail_id) {
                return this.state.query_data.list[i]
            }
        }
        return emptySystemMessage
    }

    componentDidMount() {
        this.queryCertainPage(1, 10)
    }

    SystemMessageColumn(): ColumnsType<SystemMessageModel> {
        return [
            {
                title: "ID",
                dataIndex: "id",
                key: "id",
                width: 80,
                render: (_, record) => record.id
            },
            {
                title: "用户ID",
                dataIndex: "user_id",
                key: "user_id",
                width: 100,
                render: (_, record) => record.user_id || "-"
            },
            {
                title: "角色",
                dataIndex: "role",
                key: "role",
                width: 100,
                render: (_, record) => (
                    <Tag color="blue">{getSystemMessageRoleDesc(record.role as any)}</Tag>
                )
            },
            {
                title: "消息类型",
                dataIndex: "message_type",
                key: "message_type",
                width: 100,
                render: (_, record) => record.message_type
            },
            {
                title: "消息内容",
                dataIndex: "message_content",
                key: "message_content",
                width: 300,
                render: (_, record) => {
                    const content = parseMessageContent(record.message_content);
                    return (
                        <div style={{
                            maxWidth: "280px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}>
                            {content.text}
                        </div>
                    );
                }
            },
            {
                title: "创建时间",
                dataIndex: "created_at",
                key: "created_at",
                width: 150,
                render: (_, record) => record.created_at
            },
            {
                title: "操作",
                key: "action",
                width: 150,
                render: (_, record) => (
                    <Space size="middle">
                        <Button
                            type="link"
                            icon={<EditOutlined/>}
                            onClick={() => this.handleMessageViewDetail(record.id)}
                        >
                            编辑
                        </Button>
                        <Popconfirm
                            title="确定要删除这条消息吗？"
                            onConfirm={() => this.handleDeleteMessage(record.id)}
                            okText="确定"
                            cancelText="取消"
                        >
                            <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined/>}
                            >
                                删除
                            </Button>
                        </Popconfirm>
                    </Space>
                )
            }
        ]
    }

    render() {
        return (
            <WindowParamContext.Consumer>
                {wpt => {
                    let wp = wpt.param;

                    return (
                        <ColumnUnit
                            sizeAttr={new SizeAttr(wp, "100vw")}
                            colorAttr={new ColorAttr(wp, "", "#FFFFFF")}
                            customStyleAttr={ColumnAttr.getChildrenPositionObj()}>

                            {/*详情 Dialog*/}
                            <Drawer
                                width={960}
                                title={this.state.detail_id == "0" ? "新增系统消息" : "编辑系统消息"}
                                open={this.state.detail_id != "-1"}
                                onClose={() => {
                                    this.handleOperateClose()
                                }}>
                                <ColumnUnit
                                    customStyleAttr={{"padding": "16px"}}
                                    sizeAttr={new SizeAttr(wp, "928px")}>
                                    {ModifyInputString(wp, "用户ID", this.state.op_request.user_id.toString(),
                                        (v) => {
                                            let nv = 0
                                            try {
                                                nv = Number(v)
                                            } catch (e) {
                                            }
                                            let op_request = Object.assign(
                                                this.state.op_request, {user_id: nv})
                                            this.setState(prevState => (Object.assign(
                                                prevState, {op_request: op_request})))
                                        }, "留空表示系统消息", "660px", "36px", "100px", "22px", "number",
                                        false
                                    )}
                                    {ModifyInputSelect(wp, "角色", this.state.op_request.role,
                                        (v) => {
                                            let op_request = Object.assign(
                                                this.state.op_request, {role: v})
                                            this.setState(prevState => (Object.assign(
                                                prevState, {op_request: op_request})))
                                        }, allSystemMessageRoleList.map((v) => {
                                            return {value: v, label: getSystemMessageRoleDesc(v)}
                                        }),
                                        "660px", "36px", "100px", "22px",
                                        false
                                    )}
                                    {ModifyInputString(wp, "消息类型", this.state.op_request.message_type,
                                        (v) => {
                                            let op_request = Object.assign(
                                                this.state.op_request, {message_type: v})
                                            this.setState(prevState => (Object.assign(
                                                prevState, {op_request: op_request})))
                                        }, "", "660px", "36px", "100px", "22px", "text",
                                        true
                                    )}
                                    {ModifyInputTextArea(wp, "消息内容", this.state.op_request.text_content,
                                        (v) => {
                                            let op_request = Object.assign(
                                                this.state.op_request, {text_content: v})
                                            this.setState(prevState => (Object.assign(
                                                prevState, {op_request: op_request})))
                                        }, "请输入消息内容", "660px", "", "100px", "22px", "text",
                                        false
                                    )}

                                    <Button
                                        style={{
                                            "marginTop": "36px",
                                            "width": "114px"
                                        }}
                                        onClick={() => {
                                            this.handleAddOrUpdateConfirm();
                                        }}
                                        type={"primary"}>{this.state.detail_id == "0" ? "新增" : "更新"}</Button>
                                </ColumnUnit>
                            </Drawer>

                            {/*数据板块*/}
                            <ColumnUnit
                                sizeAttr={new SizeAttr(wp, SizeAttr.pxMinus(SizeAttr.changeVwToPx(wp, "100vw"), "32px"))}
                                customStyleAttr={{"padding": "16px"}}
                            >
                                <RowUnit customStyleAttr={{"marginBottom": "16px"}}>
                                    <SpanText
                                        colorAttr={new ColorAttr(wp, "#1777FF")}
                                        customStyleAttr={{"justifyContent": ""}}
                                        fontAttr={new FontAttr(wp, "1.4em", "600")}>
                                        {"系统消息"}
                                    </SpanText>
                                </RowUnit>

                                {/*过滤字段*/}
                                <RowUnit>
                                    {InputString(wp, "用户ID", this.state.query_request.user_id?.toString() || "",
                                        (v) => {
                                            try {
                                                let query_request = Object.assign(
                                                    this.state.query_request, {user_id: v ? Number(v) : undefined})
                                                this.setState(prevState => (Object.assign(
                                                    prevState, {query_request: query_request})))
                                            } catch (e) {
                                                console.log(e)
                                            }
                                        }
                                    )}
                                    {InputSelect(wp, "角色", this.state.query_request.role || "",
                                        (v) => {
                                            let query_request = Object.assign(
                                                this.state.query_request, {role: v})
                                            this.setState(prevState => (Object.assign(
                                                prevState, {query_request: query_request})))
                                        },
                                        [...[{value: "", label: "不限"}],
                                            ...(allSystemMessageRoleList.map((v) => {
                                                return {value: v, label: getSystemMessageRoleDesc(v)}
                                            }))]
                                        , "180px"
                                    )}
                                    {InputString(wp, "消息内容", this.state.query_request.message_content || "",
                                        (v) => {
                                            try {
                                                let query_request = Object.assign(
                                                    this.state.query_request, {message_content: v})
                                                this.setState(prevState => (Object.assign(
                                                    prevState, {query_request: query_request})))
                                            } catch (e) {
                                                console.log(e)
                                            }
                                        }
                                    )}

                                    <Button
                                        style={Object.assign(RowAttr.getChildrenPositionObj(), {
                                            "marginLeft": "auto"
                                        })}
                                        icon={<PlusOutlined/>}
                                        type={"primary"}
                                        onClick={() => {
                                            this.handleAddNewMessage();
                                        }}
                                    >
                                        {"新增"}
                                    </Button>

                                    <Button
                                        style={Object.assign(RowAttr.getChildrenPositionObj(), {
                                            "marginLeft": "16px"
                                        })}
                                        icon={<SearchOutlined/>}
                                        type={"primary"}
                                        onClick={() => {
                                            this.queryCertainPage(1,
                                                this.state.query_request.page_size)
                                            let qr = this.state.query_request
                                            qr.page_num = 1
                                            this.setState(prevState => (Object.assign(prevState, {query_request: qr})))
                                        }}
                                    >
                                        {"查询"}
                                    </Button>
                                </RowUnit>

                                {heightGap(wp, "16px")}
                                <Table
                                    style={{
                                        "width": SizeAttr.pxMinus(SizeAttr.changeVwToPx(wp, "100vw"), "32px")
                                    }}
                                    pagination={{
                                        current: this.state.query_request.page_num,
                                        onChange: (page, pageSize) => {
                                            this.queryCertainPage(page, pageSize)
                                        },
                                        total: this.state.query_data.total,
                                    }}
                                    bordered={true}
                                    columns={this.SystemMessageColumn()}
                                    dataSource={this.state.query_data.list}
                                    rowKey="id"
                                />
                            </ColumnUnit>
                        </ColumnUnit>
                    )
                }
                }
            </WindowParamContext.Consumer>
        )
    }
}
